import { useQuery } from '@tanstack/react-query';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { adminService } from '../../services/loanService';
import { formatCurrency, formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// ── Shared helpers ────────────────────────────────────────────────────────────

const LOAN_STATUS_META = {
  submitted:      { label: 'Submitted',       color: '#94a3b8' },
  under_review:   { label: 'Under Review',    color: '#5B8EFF' },
  credit_check:   { label: 'Credit Check',    color: '#818CF8' },
  offer_sent:     { label: 'Offer Sent',      color: '#ff9900' },
  offer_accepted: { label: 'Offer Accepted',  color: '#F5A623' },
  approved:       { label: 'Approved',        color: '#00C896' },
  disbursed:      { label: 'Disbursed',       color: '#00C896' },
  active:         { label: 'Active',          color: '#00C896' },
  rejected:       { label: 'Rejected',        color: '#FF4757' },
  closed:         { label: 'Closed',          color: '#94a3b8' },
  defaulted:      { label: 'Defaulted',       color: '#FF4757' },
};

function Badge({ value, meta }) {
  const m = meta[value] || { label: value, color: '#94a3b8' };
  return (
    <span style={{
      fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.55rem',
      borderRadius: 99, background: `${m.color}1A`, color: m.color,
      border: `1px solid ${m.color}33`, whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
}

function StatCard({ label, value, color, to, urgent, sub, icon }) {
  const navigate = useNavigate();
  return (
    <div
      className="stat-card"
      onClick={() => to && navigate(to)}
      style={{
        cursor: to ? 'pointer' : 'default',
        transition: 'transform .15s, box-shadow .15s',
        position: 'relative', overflow: 'hidden',
        ...(urgent && { borderColor: `${color}40` }),
      }}
      onMouseEnter={e => { if (to) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 32px ${color}20`; } }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      {urgent && (
        <span style={{
          position: 'absolute', top: 8, right: 8, width: 8, height: 8,
          borderRadius: '50%', background: color,
          boxShadow: `0 0 8px ${color}`, animation: 'pulse 2s infinite',
        }} />
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div className="stat-value" style={{ color }}>{value}</div>
          <div className="stat-label">{label}</div>
          {sub && <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', marginTop: '0.25rem' }}>{sub}</div>}
          {to && <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', marginTop: '0.35rem' }}>Click to view →</div>}
        </div>
        {icon && (
          <span style={{ fontSize: '1.5rem', opacity: 0.25, marginTop: 2 }}>{icon}</span>
        )}
      </div>
    </div>
  );
}

function QuickAction({ icon, label, desc, to, color }) {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(to)}
      style={{
        padding: '1rem', borderRadius: 'var(--radius)',
        border: '1px solid var(--border)',
        background: 'var(--surface)',
        cursor: 'pointer', transition: 'all .15s',
        display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${color}50`; e.currentTarget.style.background = `${color}08`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.transform = ''; }}
    >
      <span style={{
        fontSize: '1.1rem', width: 36, height: 36, borderRadius: 10,
        background: `${color}15`, display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0,
      }}>{icon}</span>
      <div>
        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-1)' }}>{label}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 2 }}>{desc}</div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const { isMaker, isChecker, isAuditor } = useAuth();
  const navigate = useNavigate();

  if (isAuditor)              return <Navigate to="/admin/audit-logs" replace />;
  if (isMaker && !isChecker)  return <Navigate to="/admin/maker"      replace />;
  if (isChecker && !isMaker)  return <Navigate to="/admin/checker"    replace />;

  return <AdminDashboardContent navigate={navigate} />;
}

function AdminDashboardContent({ navigate }) {
  const { data: statsRaw, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => adminService.getDashboardStats().then(r => r.data.data),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const { data: loansRaw, isLoading: loansLoading } = useQuery({
    queryKey: ['admin-recent-loans'],
    queryFn: () => adminService.getAllLoans({ page_size: 8, ordering: '-created_at' }).then(r => {
      const d = r.data.data;
      return d.results ?? d ?? [];
    }),
    refetchInterval: 30_000,
    staleTime: 0,
  });

  const { data: disbRaw } = useQuery({
    queryKey: ['admin-pending-disb'],
    queryFn: () => adminService.getDisbursementQueue('pending').then(r => {
      const d = r.data.data;
      return (Array.isArray(d) ? d : d.results ?? []).slice(0, 5);
    }),
    refetchInterval: 30_000,
    staleTime: 0,
  });

  const { data: fraudRaw } = useQuery({
    queryKey: ['admin-open-fraud'],
    queryFn: () => adminService.getFraudAlerts({ status: 'open', page_size: 5 }).then(r => {
      const d = r.data.data;
      return d.results ?? d ?? [];
    }),
    refetchInterval: 30_000,
    staleTime: 0,
  });

  const s         = statsRaw || {};
  const loans     = loansRaw || [];
  const disbItems = disbRaw  || [];
  const fraudItems= fraudRaw || [];

  const val = (v) => statsLoading ? '…' : (v ?? 0);

  const alerts = [
    s.pending_approvals > 0 && {
      msg: `${s.pending_approvals} approval request${s.pending_approvals > 1 ? 's' : ''} awaiting checker decision`,
      to: '/admin/approvals', color: '#ff9900',
    },
    s.open_fraud_alerts > 0 && {
      msg: `${s.open_fraud_alerts} open fraud alert${s.open_fraud_alerts > 1 ? 's' : ''} require attention`,
      to: '/admin/fraud', color: '#FF4757',
    },
    s.pending_disbursements > 0 && {
      msg: `${s.pending_disbursements} disbursement${s.pending_disbursements > 1 ? 's' : ''} pending execution`,
      to: '/admin/disbursements', color: '#818CF8',
    },
    s.pending_kyc > 0 && {
      msg: `${s.pending_kyc} KYC submission${s.pending_kyc > 1 ? 's' : ''} pending review`,
      to: '/admin/kyc', color: '#5B8EFF',
    },
  ].filter(Boolean);

  return (
    <div>
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginTop: 2 }}>
            Platform overview — full access to all operations and settings
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>
            Auto-refreshes every 30s
          </span>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.25)',
            borderRadius: 'var(--radius)', fontSize: '0.8rem',
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#F5A623', animation: 'pulse 2s infinite', display: 'inline-block' }} />
            <span style={{ color: '#F5A623', fontWeight: 700 }}>ADMIN MODE</span>
          </div>
        </div>
      </div>

      {/* ── Alert banners ── */}
      {alerts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
          {alerts.map((a, i) => (
            <div key={i}
              onClick={() => navigate(a.to)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.75rem 1rem',
                background: `${a.color}0D`, border: `1px solid ${a.color}30`,
                borderRadius: 'var(--radius)', cursor: 'pointer', transition: 'background .15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = `${a.color}18`}
              onMouseLeave={e => e.currentTarget.style.background = `${a.color}0D`}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: a.color, flexShrink: 0, animation: 'pulse 2s infinite' }} />
              <span style={{ fontSize: '0.875rem', color: 'var(--text-1)', flex: 1 }}>{a.msg}</span>
              <span style={{ fontSize: '0.75rem', color: a.color, fontWeight: 600 }}>View →</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Operational stats (urgent) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
        <StatCard
          label="Pending Approvals"
          value={val(s.pending_approvals)}
          color="#ff9900" icon="⚖"
          to="/admin/approvals"
          urgent={s.pending_approvals > 0}
          sub="Awaiting checker review"
        />
        <StatCard
          label="Open Fraud Alerts"
          value={val(s.open_fraud_alerts)}
          color="#FF4757" icon="⚠"
          to="/admin/fraud"
          urgent={s.open_fraud_alerts > 0}
          sub="Require investigation"
        />
        <StatCard
          label="Pending Disbursements"
          value={val(s.pending_disbursements)}
          color="#818CF8" icon="₦"
          to="/admin/disbursements"
          urgent={s.pending_disbursements > 0}
          sub="Approved, awaiting transfer"
        />
        <StatCard
          label="Pending KYC Reviews"
          value={val(s.pending_kyc)}
          color="#5B8EFF" icon="✓"
          to="/admin/kyc"
          urgent={s.pending_kyc > 0}
          sub="Identity verifications"
        />
      </div>

      {/* ── Portfolio stats ── */}
      <div className="grid-4" style={{ marginBottom: '1.5rem' }}>
        <StatCard label="Total Customers"     value={val(s.total_users)}            color="#5B8EFF" icon="○" to="/admin/users"         sub="Registered borrowers" />
        <StatCard label="Total Applications"  value={val(s.total_applications)}     color="#00C896" icon="⬡" to="/admin/applications"  sub="All time" />
        <StatCard label="Pending Review"      value={val(s.pending_applications)}   color="#ff9900" icon="◎" to="/admin/applications"  sub="Awaiting processing" urgent={s.pending_applications > 0} />
        <StatCard label="Active Loans"        value={val(s.active_loans)}           color="#00C896" icon="◈" to="/admin/applications"  sub="Currently disbursed" />
        <StatCard label="Approved"            value={val(s.approved_applications)}  color="#00C896" icon="✓" to="/admin/applications"  sub="Including disbursed" />
        <StatCard label="Rejected"            value={val(s.rejected_applications)}  color="#FF4757" icon="✗" to="/admin/applications"  sub="Declined applications" />
        <StatCard label="Total Disbursed"     value={statsLoading ? '…' : formatCurrency(s.total_disbursed || 0)}      color="#F5A623" icon="▲" to="/admin/disbursements" sub="Cumulative amount" />
        <StatCard label="Repayments Received" value={statsLoading ? '…' : formatCurrency(s.total_repayments_received || 0)} color="#00C896" icon="↺" sub="Cumulative amount" />
      </div>

      {/* ── Quick actions ── */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text-1)' }}>Quick Actions</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
          <QuickAction icon="⬡" label="Review Applications"  desc="Process loan submissions and offers" to="/admin/applications"  color="#00C896" />
          <QuickAction icon="✓" label="KYC Verification"     desc="Verify customer identity documents"  to="/admin/kyc"          color="#5B8EFF" />
          <QuickAction icon="₦" label="Execute Disbursements" desc="Transfer funds to approved borrowers" to="/admin/disbursements" color="#818CF8" />
          <QuickAction icon="⚖" label="Approval Queue"       desc="Review maker-submitted requests"     to="/admin/approvals"    color="#ff9900" />
          <QuickAction icon="⚠" label="Fraud & Compliance"   desc="Investigate flagged transactions"    to="/admin/fraud"        color="#FF4757" />
          <QuickAction icon="◻" label="Role Management"      desc="Assign maker / checker access"       to="/admin/roles"        color="#F5A623" />
        </div>
      </div>

      {/* ── Recent Applications + side panels ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '1rem', alignItems: 'start' }}>

        {/* Recent applications table */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '0.95rem', fontWeight: 700 }}>Recent Applications</h2>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 2 }}>Latest 8 loan applications across all statuses</p>
            </div>
            <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }} onClick={() => navigate('/admin/applications')}>
              View All →
            </button>
          </div>

          {loansLoading ? (
            <div style={{ padding: '2rem' }}><LoadingSpinner /></div>
          ) : loans.length === 0 ? (
            <div style={{ padding: '2.5rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--text-3)' }}>No applications yet.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table style={{ fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Reference</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Applied</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loans.map(loan => (
                    <tr key={loan.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>
                          {loan.borrower_name || `${loan.borrower?.first_name ?? ''} ${loan.borrower?.last_name ?? ''}`.trim() || '—'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>
                          {loan.borrower_email || loan.borrower?.email || ''}
                        </div>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--primary)' }}>
                        {loan.application_number}
                      </td>
                      <td style={{ fontWeight: 700 }}>
                        {formatCurrency(loan.amount_requested || loan.loan_offer?.approved_amount || 0)}
                      </td>
                      <td><Badge value={loan.status} meta={LOAN_STATUS_META} /></td>
                      <td style={{ color: 'var(--text-3)', fontSize: '0.78rem' }}>{formatDate(loan.created_at)}</td>
                      <td>
                        <button
                          className="btn btn-ghost"
                          style={{ fontSize: '0.72rem', padding: '0.2rem 0.6rem' }}
                          onClick={() => navigate('/admin/applications')}
                        >
                          Open →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right column: disbursements + fraud */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Pending disbursements */}
          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: '0.875rem 1.1rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '0.88rem', fontWeight: 700 }}>
                Pending Disbursements
                {disbItems.length > 0 && (
                  <span style={{
                    marginLeft: 8, fontSize: '0.68rem', fontWeight: 700,
                    padding: '0.1rem 0.5rem', borderRadius: 99,
                    background: 'rgba(129,140,248,0.15)', color: '#818CF8',
                    border: '1px solid rgba(129,140,248,0.3)',
                  }}>{disbItems.length}</span>
                )}
              </h2>
              <button className="btn btn-ghost" style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }} onClick={() => navigate('/admin/disbursements')}>
                All →
              </button>
            </div>
            {disbItems.length === 0 ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-3)' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>✓</div>
                No pending disbursements
              </div>
            ) : (
              <div style={{ padding: '0.5rem 0' }}>
                {disbItems.map(d => (
                  <div key={d.id} style={{
                    padding: '0.625rem 1.1rem',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-1)' }}>
                        {d.applicant_name || '—'}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', fontFamily: 'monospace' }}>
                        {d.application_number || ''}
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#818CF8' }}>
                      {formatCurrency(d.net_disbursement || d.approved_amount || 0)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Open fraud alerts */}
          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: '0.875rem 1.1rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '0.88rem', fontWeight: 700 }}>
                Open Fraud Alerts
                {fraudItems.length > 0 && (
                  <span style={{
                    marginLeft: 8, fontSize: '0.68rem', fontWeight: 700,
                    padding: '0.1rem 0.5rem', borderRadius: 99,
                    background: 'rgba(255,71,87,0.15)', color: '#FF4757',
                    border: '1px solid rgba(255,71,87,0.3)',
                  }}>{fraudItems.length}</span>
                )}
              </h2>
              <button className="btn btn-ghost" style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }} onClick={() => navigate('/admin/fraud')}>
                All →
              </button>
            </div>
            {fraudItems.length === 0 ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-3)' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>✓</div>
                No open fraud alerts
              </div>
            ) : (
              <div style={{ padding: '0.5rem 0' }}>
                {fraudItems.map(f => (
                  <div key={f.id} style={{
                    padding: '0.625rem 1.1rem',
                    borderBottom: '1px solid var(--border)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-1)', flex: 1, marginRight: 8 }}>
                        {f.title || f.description?.slice(0, 40) || 'Fraud Alert'}
                      </div>
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.45rem',
                        borderRadius: 99, background: 'rgba(255,71,87,0.12)', color: '#FF4757',
                        border: '1px solid rgba(255,71,87,0.25)', flexShrink: 0,
                      }}>
                        {f.severity || f.status || 'open'}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', marginTop: 2 }}>
                      {formatDate(f.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Role note ── */}
      <div style={{
        marginTop: '1.5rem',
        padding: '0.875rem 1rem',
        background: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.2)',
        borderRadius: 'var(--radius)', fontSize: '0.8rem', color: 'var(--text-2)',
      }}>
        <strong style={{ color: '#F5A623' }}>Admin Role</strong> — You have full platform access. To assign maker or checker duties to a staff member, use{' '}
        <span
          style={{ color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => navigate('/admin/roles')}
        >
          Role Management
        </span>.
      </div>
    </div>
  );
}
